// Comments and suggestions as the margin and Revise ▸ Comments list them (the Mac's `MarginItem`): open top-level
// comments with their replies, open suggestions, and (in Revise) open synopsis comments, per fragment in reading
// order, each scene's by where it's anchored. Where a passage is now comes from its anchor (`currentRange`: scalars
// at `anchorHeads`, through Automerge's cursors, to the current text), in UTF-16 for the editor; an anchor that
// resolves to nothing is "detached" (the passage was cut, or its history hasn't reached this computer) and listed
// with its quote. Resolutions are kept per document object, so a rebuild reads again only fragments that changed.
import type * as A from '@automerge/automerge';
import type { Comment, OpenedProject } from '@writing-suite/core';
import { categoryInk, categoryOf, type CommentCategory } from '@writing-suite/core/revise';
import { currentRangeUTF16, suggestionRange, utf16OffsetOfScalar, type Anchor } from '@writing-suite/core';
import type { Annotation } from '@writing-suite/editor';

export interface MarginEntry {
  kind: 'comment' | 'suggestion' | 'synopsis';
  id: string;
  fragmentId: string;
  sceneTitle: string;
  author: string;
  authorKind: 'human' | 'agent';
  createdAt: Date;
  quote: string;
  /** A comment's text, a suggestion's note. */
  body: string;
  /** A suggestion's replacement. */
  proposed?: string;
  replies: Comment[];
  /** A comment's category (an id in the project's list); null for none, and for suggestions. */
  category: string | null;
  /** Where the passage is now, UTF-16 in the fragment's current text; null when it can't be found; undefined when
   *  not looked for (a list that finds it when it's chosen). */
  range: [number, number] | null | undefined;
  /** Where it was anchored (scalars at its heads): orders a scene's notes, as the Mac's list does. */
  anchorStart: number;
  /** For Show: the anchor as stored. */
  anchor: Pick<Comment, 'anchorStart' | 'anchorEnd' | 'anchorHeads'>;
}

/** Per document object: the record the entries came from and the entries (valid while both are the same). */
const cache = new WeakMap<object, { record: object; entries: MarginEntry[] }>();

/** Where a suggestion's passage is now, in UTF-16 (an insertion as an empty range). */
function suggestionRangeUTF16(doc: A.Doc<unknown>, s: Anchor): [number, number] | null {
  const r = suggestionRange(doc, s);
  if (!r) return null;
  const text = (doc as { text?: unknown }).text;
  const t = typeof text === 'string' ? text : '';
  return [utf16OffsetOfScalar(t, r[0]), utf16OffsetOfScalar(t, r[1])];
}

/**
 * One fragment's open comments and suggestions. `doc` is the document to resolve against (the live one, whose text
 * the editor has); `resolve: false` leaves ranges unresolved (a list that resolves one when it's chosen).
 */
export function fragmentEntries(project: OpenedProject, fragmentId: string, doc: A.Doc<unknown> | null, o: { resolve?: boolean; synopsis?: boolean } = {}): MarginEntry[] {
  const resolve = o.resolve !== false && !!doc;
  const record = project.fragmentRecord(fragmentId);
  if (!record) return [];
  const key = resolve ? doc! : null;
  if (key && !o.synopsis) {
    const hit = cache.get(key);
    if (hit && hit.record === record) return hit.entries;
  }
  const title = record.title || 'Untitled';
  const out: MarginEntry[] = [];
  if (o.synopsis) {
    for (const c of record.synopsisComments) {
      if (c.status !== 'open' || c.parentId !== null) continue;
      out.push({
        kind: 'synopsis', id: c.id, fragmentId, sceneTitle: title, author: c.author, authorKind: c.authorKind, createdAt: c.createdAt,
        quote: c.quote, body: c.body, replies: [], category: c.category, range: undefined, anchorStart: 0, anchor: c,
      });
    }
  }
  const replies = new Map<string, Comment[]>();
  for (const c of record.comments) if (c.parentId !== null) replies.set(c.parentId, [...(replies.get(c.parentId) ?? []), c]);
  for (const c of record.comments) {
    if (c.status !== 'open' || c.parentId !== null) continue;
    out.push({
      kind: 'comment', id: c.id, fragmentId, sceneTitle: title, author: c.author, authorKind: c.authorKind, createdAt: c.createdAt,
      quote: c.quote, body: c.body, replies: replies.get(c.id) ?? [], category: c.category, range: resolve ? currentRangeUTF16(doc!, c) : undefined,
      anchorStart: c.anchorStart, anchor: c,
    });
  }
  for (const s of record.suggestions) {
    if (s.status !== 'open') continue;
    out.push({
      kind: 'suggestion', id: s.id, fragmentId, sceneTitle: title, author: s.author, authorKind: s.authorKind, createdAt: s.createdAt,
      quote: s.quote, body: s.note, proposed: s.proposedText, replies: [], category: null, range: resolve ? suggestionRangeUTF16(doc!, s) : undefined,
      anchorStart: s.anchorStart, anchor: s,
    });
  }
  out.sort((a, b) => a.anchorStart - b.anchorStart || a.createdAt.getTime() - b.createdAt.getTime());
  if (key && !o.synopsis) cache.set(key, { record, entries: out });
  return out;
}

/** Resolves one entry now (Revise's Show). */
export function resolveEntry(doc: A.Doc<unknown>, e: MarginEntry): [number, number] | null {
  if (e.kind === 'synopsis') return null;
  return e.kind === 'suggestion' ? suggestionRangeUTF16(doc, e.anchor) : currentRangeUTF16(doc, e.anchor);
}

/** The editor's annotations for these entries (the ones found in the text); a comment in one of `categories` carries
 *  the category's ink, which the editor underlines it in. */
export function annotationsOf(entries: MarginEntry[], categories: readonly CommentCategory[] = []): Annotation[] {
  return entries.flatMap((e): Annotation[] => {
    if (!e.range || e.kind === 'synopsis') return [];
    const category = e.kind === 'comment' ? categoryOf(e, categories) : null;
    return [{
      id: e.id, fragmentId: e.fragmentId, from: e.range[0], to: e.range[1],
      kind: e.kind === 'suggestion' ? 'suggestion' : e.authorKind === 'agent' ? 'agent' : 'comment',
      ...(e.kind === 'suggestion' ? { proposed: e.proposed ?? '' } : {}),
      ...(category ? { ink: categoryInk(category) } : {}),
    }];
  });
}

export const isDetached = (e: MarginEntry) => e.kind !== 'synopsis' && e.range === null;
